import { useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntity } from "@/context/EntityContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, Home, Briefcase, LogOut, ArrowRightLeft } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout } = useAuth();
  const { currentEntity, refresh, isConsolidated, setEntity } = useEntity();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [savingWs, setSavingWs] = useState(false);
  const [signOutEverywhereOpen, setSignOutEverywhereOpen] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const activeType = currentEntity?.workspace_type || null;

  const signOutEverywhere = async () => {
    setSigningOutAll(true);
    try {
      await api.post("/auth/sign-out-everywhere");
      toast.success("Signed out of every device");
      // Clear this device's client cache too and bounce to /login.
      // Silent-by-design: the backend has already invalidated the token, so a
      // logout() 401 is expected. Log at debug so it's still traceable.
      try { await logout(); } catch (e) { console.debug("post-signout logout noop:", e?.message); }
      window.location.href = "/login";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign out everywhere");
      setSigningOutAll(false);
      setSignOutEverywhereOpen(false);
    }
  };

  const switchWorkspaceType = async (nextType) => {
    if (activeType === nextType) return;
    const label = nextType === "business" ? "Business" : "Personal";
    if (!window.confirm(
      `Open your ${label} workspace?\n\n`
      + `${label} keeps its OWN separate books — transactions, invoices, and bank`
      + ` balances stay isolated from your other workspace.`
    )) return;
    setSavingWs(true);
    try {
      // iter 72c — always ensure the user has ONE personal + ONE business
      // entity with strictly isolated data. Never flip the current entity's
      // workspace_type (that was the source of the bleed).
      const { data } = await api.post("/entities/ensure-workspace-pair");
      const target = nextType === "personal" ? data.personal : data.business;
      await refresh();
      toast.success(`Switched to your ${label} workspace`);
      setEntity(target.id);  // triggers a reload with the new X-Entity-Id
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch workspace");
      setSavingWs(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.next.length < 8) return setErr("New password must be at least 8 characters.");
    if (pw.next !== pw.confirm) return setErr("Passwords don't match.");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Account</div>
        <h1 className="font-display text-4xl mt-2">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Workspace switcher (iter 72c) — cross-workspace navigation.
            NOT a "flip my current entity" toggle. Each workspace has its
            own strictly-isolated books (transactions, invoices, banks). */}
        {!isConsolidated && currentEntity && (
          <div className="card-flat p-6 lg:col-span-2" data-testid="settings-workspace-card">
            <div className="flex items-center gap-2 text-[var(--brand)]">
              <ArrowRightLeft className="w-4 h-4" />
              <div className="text-xs tracking-[0.2em] uppercase">Workspace</div>
            </div>
            <h3 className="font-display text-xl mt-2">
              Currently in · {activeType === "personal" ? "🏠 Personal" : activeType === "business" ? "💼 Business" : currentEntity.name}
            </h3>
            <p className="text-sm text-[var(--muted-text)] mt-1">
              Personal and Business are <span className="font-medium">separate books</span> — transactions, invoices, and bank balances never mix. Switch to open the other one.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                data-testid="settings-workspace-personal"
                disabled={savingWs || activeType === "personal"}
                onClick={() => switchWorkspaceType("personal")}
                className={`text-left p-4 rounded-xl border-2 transition-colors disabled:cursor-default ${
                  activeType === "personal"
                    ? "border-[var(--brand)] bg-[var(--brand-light)]"
                    : "border-[var(--border)] hover:border-[var(--brand)] hover:bg-[var(--brand-light)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[var(--brand-light)] text-[var(--brand)] flex items-center justify-center">
                    <Home className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Workspace</div>
                    <div className="font-medium">Personal</div>
                  </div>
                  {activeType === "personal" && (
                    <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-[var(--brand)] text-white">Active</span>
                  )}
                </div>
                <p className="text-xs text-[var(--muted-text)] mt-3 leading-relaxed">
                  Budgets · Goals · Investments · Transactions · Categories · AI Insights · Bank recon
                </p>
              </button>
              <button
                type="button"
                data-testid="settings-workspace-business"
                disabled={savingWs || activeType === "business"}
                onClick={() => switchWorkspaceType("business")}
                className={`text-left p-4 rounded-xl border-2 transition-colors disabled:cursor-default ${
                  activeType === "business"
                    ? "border-[var(--brand)] bg-[var(--brand-light)]"
                    : "border-[var(--border)] hover:border-[var(--brand)] hover:bg-[var(--brand-light)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[var(--brand-light)] text-[var(--brand)] flex items-center justify-center">
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Workspace</div>
                    <div className="font-medium">Business</div>
                  </div>
                  {activeType === "business" && (
                    <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-[var(--brand)] text-white">Active</span>
                  )}
                </div>
                <p className="text-xs text-[var(--muted-text)] mt-3 leading-relaxed">
                  Everything Personal + Customers & Invoices · Vendors & Bills · Payroll · Inventory · Fixed Assets · Executive KPIs · Team RBAC
                </p>
              </button>
            </div>
            {savingWs && (
              <div className="text-xs text-[var(--muted-text)] mt-3">Switching workspace…</div>
            )}
          </div>
        )}

        <div className="card-flat p-6">
          <div className="flex items-center gap-2 text-[var(--brand)]">
            <ShieldCheck className="w-4 h-4" />
            <div className="text-xs tracking-[0.2em] uppercase">Profile</div>
          </div>
          <h3 className="font-display text-xl mt-2">Your details</h3>
          <div className="mt-4 space-y-3">
            <div>
              <div className="text-xs text-[var(--muted-text)]">Name</div>
              <div className="text-sm font-medium mt-1">{user?.name}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--muted-text)]">Email</div>
              <div className="text-sm font-medium mt-1">{user?.email}</div>
            </div>
            <div>
              <div className="text-xs text-[var(--muted-text)]">Role</div>
              <div className="text-sm font-medium mt-1 capitalize">{user?.role || "user"}</div>
            </div>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="flex items-center gap-2 text-[var(--brand)]">
            <KeyRound className="w-4 h-4" />
            <div className="text-xs tracking-[0.2em] uppercase">Security</div>
          </div>
          <h3 className="font-display text-xl mt-2">Change password</h3>
          <form data-testid="change-password-form" onSubmit={submit} className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="cur">Current password</Label>
              <Input
                id="cur"
                data-testid="cp-current"
                type="password" required
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New password</Label>
              <Input
                id="new"
                data-testid="cp-new"
                type="password" required minLength={8}
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                placeholder="8+ characters"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnf">Confirm new password</Label>
              <Input
                id="cnf"
                data-testid="cp-confirm"
                type="password" required minLength={8}
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              />
            </div>
            {err && <div data-testid="cp-error" className="text-sm text-[#E63946]">{err}</div>}
            <Button
              data-testid="cp-submit"
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              {submitting ? "Updating..." : "Update password"}
            </Button>
          </form>
        </div>

        {/* Sign out everywhere */}
        <div className="card-flat p-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Session control</div>
          <h3 className="font-display text-xl mt-2">Sign out everywhere</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
            Instantly ends every open session on every device and browser tied to your account —
            including this one. Use this if you lost a phone or suspect your credentials are compromised.
            You'll need to log in again on this device afterwards.
          </p>
          <Button
            data-testid="sign-out-everywhere-btn"
            variant="outline"
            className="mt-4 rounded-full h-11 px-5 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
            onClick={() => setSignOutEverywhereOpen(true)}
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out of every device
          </Button>
        </div>

        <AlertDialog open={signOutEverywhereOpen} onOpenChange={setSignOutEverywhereOpen}>
          <AlertDialogContent data-testid="sign-out-everywhere-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Sign out of every device?</AlertDialogTitle>
              <AlertDialogDescription>
                This ends every active session for your account, including this browser.
                You'll be logged out immediately and every other device will need a fresh login too.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="sign-out-everywhere-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="sign-out-everywhere-confirm"
                onClick={signOutEverywhere}
                disabled={signingOutAll}
                className="bg-[#E63946] hover:bg-[#c92b38]"
              >
                {signingOutAll ? "Signing out..." : "Yes, sign me out everywhere"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
